import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { NodeId } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import {
  type GraphNodeDisplayObject,
  getNodeId,
} from 'components/Graph/GraphApp/GraphNode';
import { MultiClickDispatcher } from 'components/Graph/GraphApp/helpers/MultiClickDispatcher';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { uncollapseNode } from 'components/Graph/commands/collapsing/uncollapseNode';
import { expandGraph } from 'components/Graph/commands/expandGraph';
import { toggleExpandDeviceGroup } from 'components/Graph/commands/toggleExpandDeviceGroup';
import { toggleSelectNode } from 'components/Graph/commands/toggleSelectNode';
import { isNodeExpandable } from 'components/Graph/helpers/IsDeviceExpandable';
import { isNodeWalkable } from 'components/Graph/helpers/isNodeWalkable';
import { isCollapsedNodeId } from 'components/Graph/model/GraphData';
import { hasOwnProperty as recordHasOwnProperty } from 'fp-ts/lib/Record';
import { flow } from 'fp-ts/lib/function';

export const nodeClick: GraphRendererPlugin = (pluginInterface) => {
  const getClickedNodeId = (node: GraphNodeDisplayObject): NodeId | null => {
    const nodeId = getNodeId(node);
    if (!recordHasOwnProperty(nodeId, pluginInterface.getGraphData().nodes)) {
      stumble('Clicked node that is not present in data');
      return null;
    }
    return nodeId;
  };

  const handleNodeClick = (nodeId: NodeId) => {
    pluginInterface.graphDispatch(toggleSelectNode, {
      nodeId,
      // toggle in selection mode, always select otherwise
      select: pluginInterface.isSelectionMode() ? undefined : true,
    });
  };

  const handleNodeDoubleClick: (nodeId: NodeId) => void = flow(
    // cannot use propOf(pluginInterface.getGraphData().nodes) as it
    // would hold reference to initial graphData and not receive updates.
    // Fixed noUncheckedIndexedAccess NIM-19408
    (nodeId) => pluginInterface.getGraphData().nodes[nodeId]!,
    (nodeData) => {
      if (pluginInterface.isSelectionMode()) {
        return;
      }
      if (isNodeWalkable(nodeData)) {
        pluginInterface.graphDispatch(expandGraph, { path: nodeData.path });
      } else if (
        isCollapsedNodeId(pluginInterface.getGraphData().collapsedNodeGroups)(
          nodeData.id,
        )
      ) {
        pluginInterface.graphDispatch(uncollapseNode, { nodeId: nodeData.id });
      } else if (isNodeExpandable(nodeData)) {
        pluginInterface.graphDispatch(toggleExpandDeviceGroup, { nodeData });
      }
    },
  );

  const clickDispatcher = new MultiClickDispatcher<GraphNodeDisplayObject>(
    pluginInterface,
    {
      onClick: flow(getClickedNodeId, nullable.chain(handleNodeClick)),
      onDoubleClick: flow(
        getClickedNodeId,
        nullable.chain(handleNodeDoubleClick),
      ),
    },
  );

  pluginInterface.events.addListener(
    'nodeMouseDown',
    clickDispatcher.handleMouseDown,
  );

  pluginInterface.events.addListener(
    'interruptClick',
    clickDispatcher.interruptClick,
  );
};
